/**
 * @file   src\Pages\AddBuyupRelayNumber.tsx
 * @brief  Add/Update relay numbers for buy ups.
 * @date   JAN, 2026
 * @author ZCO Engineer
 * @copyright (c) 2026, ZCO
 */

import {
  useEffect,
  useNavigate,
  useState,
  useLocation,
  useOutletContext,
  Container,
  Table,
  Button,
  Breadcrumb,
  useParams,
  React,
} from "../utils/thirdpartyComponents";
import "../styles/subscriptions.scss";
import CWSelect from "../components/CWSelect";
import { pageURLs } from "../utils/pageUrls";
import { AppContextType } from "../interfaces/generalInterface";
import { MessageToaster } from "../utils/toastUtil";
import axios from "../config/axiosConfig";
import { CompanyApi, LocationApi, SubscriptionApi } from "../utils/apiUrls";
import FullLoader from "../components/FullLoader";
import { LocationItem as RelayLocationItem } from "../interfaces/relayDataInterface";
import { userType } from "../utils/enums";

interface SelectOption {
  value: number;
  label: string;
}

interface BuyupRelayRow {
  locationId: number;
  locationName: string;
  buyupName: string;
  relayNumber: number | null;
}

const AddBuyupRelayNumber = (props: any) => {
  const { updateData } = useOutletContext<AppContextType>();
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const bayType = query.get("bayType") ?? "0";
  const param = useParams();
  const toaster = new MessageToaster();

  const options = [
    { label: "01", value: 1 },
    { label: "02", value: 2 },
    { label: "03", value: 3 },
    { label: "04", value: 4 },
    { label: "05", value: 5 },
    { label: "06", value: 6 },
    { label: "07", value: 7 },
    { label: "08", value: 8 },
    { label: "09", value: 9 },
    { label: "10", value: 10 },
    { label: "11", value: 11 },
    { label: "12", value: 12 },
  ];

  const [loading, setLoading] = useState(false);
  const [company, setCompany] = useState({
    companyName: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    zipCode: "",
  });
  const [locations, setLocations] = useState<{ value: number; label: string }[]>(
    []
  );
  const [relayData, setRelayData] = useState<BuyupRelayRow[]>([]);
  const [selectedRelays, setSelectedRelays] = useState({});
  const [error, setError] = useState<Record<number, { message: string }>>({});

  const buyupId = props.userType !== userType["CARWASH-OWNER"]
    ? parseInt((param as any).buyupId ?? "0")
    : parseInt(param.buyupId ?? "0");
    console.log(buyupId);
    console.log(param);
  const carWashId =
    props.userType !== userType["CARWASH-OWNER"]
      ? parseInt(param.id ?? "0")
      : 0;

  useEffect(() => {
    getCompanyAddress();
    getCompanyLocations();
  }, []);

  useEffect(() => {
    handleUpdate();
  }, [company]);

  useEffect(() => {
    if (locations.length) {
      getBuyupDetails();
    }
  }, [locations]);

  const handleUpdate = () => {
    updateData({
      heading:
        props.userType !== userType["CARWASH-OWNER"]
          ? company.companyName
          : "Relay Details",
      url:
        props.userType !== userType["CARWASH-OWNER"]
          ? `/superadmin/${pageURLs.manage_subscriptions}/${param.id}?type=buyups&bayType=${bayType}`
          : `/${pageURLs.manage_subscriptions}?type=buyups&bayType=${bayType}`,
      showBackButton: true,
      showMainMenu: true,
      activeLink:
        props.userType !== userType["CARWASH-OWNER"]
          ? "manage-car-wash"
          : "manage-subscriptions",
    });
  };

  const getCompanyAddress = async () => {
    try {
      const id = param.id ? parseInt(param.id) : 0;
      const { data } =
        props.userType !== userType["CARWASH-OWNER"]
          ? await axios.get(CompanyApi.GET_COMPANY_ADDRESS + id)
          : await axios.get(CompanyApi.CARWASH_OWNER_COMPANY_ADDRESS);
      if (data?.error === 0) {
        setCompany(data.data);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  const getCompanyLocations = async () => {
    try {
      if (props.userType !== userType["CARWASH-OWNER"]) {
        const { data } = await axios.get(CompanyApi.GET_COMPANY + `${param.id}`);
        const locationsRes = await axios.get(
          LocationApi.GET_COMPANY_LOCATIONS + data.data.carWashGUID
        );
        if (locationsRes?.data?.error === 0) {
          const transformed = locationsRes.data.data.map((item: any) => ({
            label: item.locationName,
            value: item.locationId,
          }));
          setLocations(transformed);
        }
      } else {
        const { data } = await axios.get(
          LocationApi.CARWASH_OWNER_GET_COMPANY_LOCATIONS
        );
        if (data?.error === 0) {
          const transformed = data.data.map((item: any) => ({
            label: item.locationName,
            value: item.locationId,
          }));
          setLocations(transformed);
        }
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    }
  };

  const getBuyupDetails = async () => {
    try {
      setLoading(true);
      const detailsUrl =
        props.userType !== userType["CARWASH-OWNER"]
          ? SubscriptionApi.GET_BUY_UP_RELAY_DETAILS
          : SubscriptionApi.CARWASH_OWNER_GET_BUY_UP_RELAY_DETAILS;
      const { data } = await axios.get(`${detailsUrl}${buyupId}`);
      if (data?.error === 0 && Array.isArray(data?.data)) {
        const buyupDetailsUrl =
          props.userType !== userType["CARWASH-OWNER"]
            ? SubscriptionApi.GET_BUY_UP_DETAILS
            : SubscriptionApi.CARWASH_OWNER_GET_BUY_UP_DETAILS;
        const buyupRes = await axios.get(`${buyupDetailsUrl}${buyupId}`);
        const buyupName = buyupRes?.data?.data?.BuyupName || "";
        const rows: BuyupRelayRow[] = data.data.map((item: any) => {
          const locationId = item.LocationID ?? 0;
          const locationName =
            locations.find((loc) => loc.value === locationId)?.label || "";
          return {
            locationId,
            locationName,
            buyupName,
            relayNumber: item.RelayNumber ?? null,
          };
        });
        setRelayData(rows);
      } else {
        setRelayData([]);
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectChange = (
    index: number,
    selectedOption: SelectOption,
    item: BuyupRelayRow
  ) => {
    const updated = [...relayData];
    updated[index].relayNumber = selectedOption.value;
    setRelayData(updated);
    setError((prev) => ({ ...prev, [index]: { message: "" } }));
    setSelectedRelays((prevSelectedRelays) => ({
      ...prevSelectedRelays,
      [index]: {
        relayNumber: selectedOption.value,
        location_id: item.locationId,
      },
    }));
  };

  const validateRelayNumbers = (): boolean => {
    let isValid = true;
    const errors: Record<number, { message: string }> = {};
    relayData.forEach((item, index) => {
      if (!item.relayNumber) {
        isValid = false;
        errors[index] = { message: "Relay number is required." };
      }
    });
    setError(errors);
    return isValid;
  };

  const saveRelayNumber = async () => {
    try {
      if (validateRelayNumbers()) {
        // Always send all rows so edit-save without changes does not submit an empty locations array.
        const locationsPayload = relayData.map((item) => ({
          locationId: item.locationId,
          relayNumber: item.relayNumber as number,
        }));
        const payload = {
          buyupId: buyupId,
          locations: locationsPayload,
        };
        const { data } =
          props.userType !== userType["CARWASH-OWNER"]
            ? await axios.post(SubscriptionApi.SAVE_BUY_UP_RELAY_NUMBER, payload)
            : await axios.post(
                SubscriptionApi.CARWASH_OWNER_SAVE_BUY_UP_RELAY_NUMBER,
                payload
              );
        if (data?.error === 0) {
          toaster.toastSuccess(data?.message || "");
          if (props.userType !== userType["CARWASH-OWNER"]) {
            navigate(
              `/superadmin/${pageURLs.manage_subscriptions}/${param.id}?bayType=${bayType}&type=buyups`
            );
          } else {
            navigate(
              `/${pageURLs.manage_subscriptions}?bayType=${bayType}&type=buyups`
            );
          }
        } else {
          toaster.toastError(data?.message);
        }
      }
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || error?.message);
    }
  };

  return (
    <div className="manage-carwash-container manage-subscription-container">
      <Container>
        <Breadcrumb>
          {props.userType !== userType["CARWASH-OWNER"] ? (
            <>
              <Breadcrumb.Item
                onClick={() => {
                  navigate(`/superadmin/${pageURLs.manage_carwash}`);
                }}
              >
                Manage Car Washes
              </Breadcrumb.Item>
              <Breadcrumb.Item
                onClick={() => {
                  navigate(`/superadmin/${pageURLs.carwash_locations}/${param.id}`);
                }}
              >
                {company.companyName}
              </Breadcrumb.Item>
            </>
          ) : null}

          <Breadcrumb.Item
            onClick={() =>
              props.userType !== userType["CARWASH-OWNER"]
                ? navigate(
                    `/superadmin/${pageURLs.manage_subscriptions}/${param.id}?type=buyups&bayType=${bayType}`
                  )
                : navigate(
                    `/${pageURLs.manage_subscriptions}?type=buyups&bayType=${bayType}`
                  )
            }
          >
            Manage Up Sell
          </Breadcrumb.Item>

          <Breadcrumb.Item active>Relay Details</Breadcrumb.Item>
        </Breadcrumb>

        <div className="cw-table-wrap tbl-visible">
          <Table responsive className="m-0">
            <thead>
              <tr>
                <th>Car Wash Location</th>
                <th>Up Sell Name</th>
                <th>Relay Number</th>
              </tr>
            </thead>
            <tbody>
              {relayData.map((item, index) => (
                <tr key={index}>
                  <td>{item.locationName}</td>
                  <td>{item.buyupName}</td>
                  <td>
                    <CWSelect
                      id="relaynumber"
                      name="relaynumber"
                      options={options}
                      dropdownGap={0}
                      dropdownPosition="auto"
                      className="hide-input"
                      multi={false}
                      errorMessage={error[index]?.message && error[index].message}
                      values={options.filter(
                        (option) => option.value === item.relayNumber
                      )}
                      onChange={(selected) =>
                        handleSelectChange(index, selected[0], item)
                      }
                      isMandatory
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <div className="table-btn-action text-end">
          <Button
            title="Save"
            variant="primary"
            className="primary-btn"
            onClick={() => saveRelayNumber()}
          >
            Save
          </Button>
        </div>
      </Container>
      {loading && <FullLoader />}
    </div>
  );
};

export default AddBuyupRelayNumber;


